<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaigns.focusPointList.modal.new.buttonText')"
      @click="openFocusPointModal = true"
    />
    <export
      url="/focus_points/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <focus-point-modal
    v-model:open="openFocusPointModal"
    :campaign-group="modelValue"
    v-model:focus-point="editFocusPoint"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <focus-points-map class="ml-12" v-if="records?.length" :busy="isLoading" :points="pointsForMap" />

    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.campaigns.focusPointList.tableHeader') }}
      </div>

      <div class="table">
        <data-table
          class="mb-auto"
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          v-model:checked-ids-list="checkedIdsList"
          :row-link="
          (item) => ({
            name: 'campaign-group-focus-point-details',
            params: { focusPointId: item.id },
          })
        "
          :busy="isLoading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
        />
      </div>
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, type Ref, unref} from 'vue';
import AddIcon from '@images/panes/add.svg?component';
import Action from '@utils/toolbar/Action.vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import FocusPointsMap from '@/pages/campaigns/campaignGroup/FocusPointsMap.vue';
import type {Action as ActionType} from '@components/utils/table/Table.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {useProvider} from '@components/utils/table/hooks/provider';
import type {Filter} from '@utils/filter/Filter.vue';
import type {CampaignGroup, FocusPoint, HydraCollection} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import FocusPointModal from '@pages/campaigns/campaignGroup/FocusPointModal.vue';
import {useDelete} from '@hooks/api';

import Export from '@utils/toolbar/panes/Export.vue';
import {format} from 'date-fns';
import {useQueryRef} from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);

const openFocusPointModal = ref(false);
const editFocusPoint = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.focusPointsFromGroup', {
    group: props.modelValue?.name,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaign.group': props.modelValue?.id ?? '',
  'inArray[campaign.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const [deleteFocusPoint, { loading: deleting }] = useDelete();

const actions: ActionType[] = [
  {
    title: t('pages.campaigns.focusPointList.actions.edit'),
    action: (item: FocusPoint) => {
      editFocusPoint.value = { ...item, campaign: item.campaign?.['@id'] };
      openFocusPointModal.value = true;
    },
  },
  {
    title: t('pages.campaigns.focusPointList.actions.delete'),
    action: async (item: FocusPoint) => {
      if (await deleteFocusPoint(`/focus_points/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'name',
    title: t('focusPoint.name'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        label: t('focusPoint.name'),
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaign',
      type: 'dropdown',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))
        ),
        label: t('campaign.DEFAULT'),
      },
    },
  },
  {
    key: 'campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'campaign.timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
    },
    title: t('campaign.timeStatus.DEFAULT2'),
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (fp: FocusPoint) => {
      const now = new Date();
      if (!fp.campaign) {
        return '';
      }
      const startDate = new Date(fp.campaign.actualStartDate);
      const endDate = new Date(fp.campaign.actualEndDate);
      if(startDate <= now && endDate >= now) {
        return t('campaign.timeStatus.ongoing');
      } else if(startDate > now) {
        return t('campaign.timeStatus.future');
      } else {
        return t('campaign.timeStatus.past');
      }
    },
    filter: {
      key: 'timeStatus',
      type: 'dropdown',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT2'),
      },
    },
  },
  {
    key: 'radius',
    title: t('focusPoint.radius'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        icon: <span>m</span>,
        label: t('focusPoint.radius'),
      },
    },
  },
  {
    key: 'gisLocation.lat',
    title: t('focusPoint.gisLocation.latitude'),
    sortable: false,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
  },
  {
    key: 'gisLocation.long',
    title: t('focusPoint.gisLocation.longitude'),
    sortable: false,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
  },
  {
    key: 'threshold',
    title: t('focusPoint.threshold'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    formatter: (v: number) => (v ? formatTimeDurationFromSeconds(v) : ''),
    filter: {
      type: 'duration',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('focusPoint.threshold'),
      },
    },
  },
  {
    key: 'cardinality',
    title: t('focusPoint.cardinality'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        label: t('focusPoint.cardinality'),
      },
    },
  },
  {
    key: 'paymentAmount',
    title: t('focusPoint.paymentAmount'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    unit: 'CHF',
    formatter: (v: number) => (v ? v.toFixed(2).padStart(5, '0') + ' CHF' : ''),
    filter: {
      type: 'range',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        icon: <span>CHF</span>,
        label: t('focusPoint.paymentAmount'),
      },
    },
  },
  {
    key: 'numOfPhotos',
    title: t('focusPoint.numOfPhotos'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.focusPointList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        label: t('focusPoint.numOfPhotos'),
      },
    },
  },
  {
    key: 'validityStart',
    title: t('focusPoint.validityStart'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.campaigns.focusPointList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        label: t('focusPoint.validityStart'),
      },
    },
  },
  {
    key: 'validityEnd',
    title: t('focusPoint.validityEnd'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.campaigns.focusPointList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.focusPointList.filterGroups.general'),
      options: {
        label: t('focusPoint.validityEnd'),
      },
    },
  },
  {
    key: 'statistics.totalStops',
    title: t('focusPoint.totalStops'),
    group: t('pages.campaigns.focusPointList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
  },
  {
    key: 'statistics.totalTime',
    title: t('focusPoint.totalTime'),
    group: t('pages.campaigns.focusPointList.fieldGroups.statistics'),
    formatter: (v: number) => (v ? formatTimeDurationFromSeconds(v) : '00:00:00'),
    visible: true,
    sortable: false,
  },
  {
    key: 'statistics.totalPhotos',
    title: t('focusPoint.totalPhotos'),
    group: t('pages.campaigns.focusPointList.fieldGroups.statistics'),
    visible: true,
    sortable: false,
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<FocusPoint>>('/focus_points', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const isLoading = computed(() => loading.value || deleting.value);

const checkedIdsList = ref<string[]>([]);
const pointsForMap = computed(() => checkedIdsList.value.map((id) => records.value?.find((record) => record.id === id)));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

